import { Injectable } from '@angular/core';
import {
    EstudioParameters,
    EstudioReports,
    EstudioTemplate,
} from '../entities/estudio-model';
import {
    UntypedFormGroup,
    UntypedFormBuilder,
    UntypedFormControl,
    Validators,
} from '@angular/forms';
import { DateHelper } from 'app/modules/common/helpers/DateHelper';
import moment, { Moment } from 'moment';
import API from '../../common/services/api.config.json';
import { ApiService } from 'app/modules/common/services/api.service';
import { Observable } from 'rxjs';
import { RestResponse } from 'app/modules/common/entities/rest.response.generic.model';
import { map } from 'rxjs/operators';
import { EmpresaTreeViewService } from 'app/modules/employee/services/empresa-treeView.service';
import { SharedService } from 'app/modules/common/services/shared-service';
import { EstudioEmpresa } from 'app/modules/common/entities/estudio-predefinition.model';

type PredefinitionParameter = {
    FormValues: any;
    LeaveType: any;
    AttributeList: any[];
};

@Injectable({
    providedIn: 'root',
})
export class ReportsEstudioService {
    public estudioParameters = new EstudioParameters();
    public estudioTemplate = new EstudioTemplate();

    public estudioParameterForm: UntypedFormGroup;
    public ReportDateFrom: UntypedFormControl;
    public ReportDateTo: UntypedFormControl;
    public LeaveType: UntypedFormControl;
    public AttributeList: any[];

    constructor(
        private formBuilder: UntypedFormBuilder,
        private apiService: ApiService,
        private empresaTreeViewService: EmpresaTreeViewService,
        private sharedService: SharedService
    ) {}

    initEstudioParamterForm(): void {
        this.estudioParameterForm = this.formBuilder.group({
            CompanyStartDate: [null],
            CompareWithPrevious: [true],
            CompanyLevel: [null],
            ReportLanguage: [0, Validators.required],
            Report5YearAgeGap: [false],
            DiasBaja: [0],
        });
        this.ReportDateFrom = new UntypedFormControl(null, Validators.required);
        this.ReportDateTo = new UntypedFormControl(null, Validators.required);
        this.LeaveType = new UntypedFormControl(null);
        this.AttributeList = [];

        // Set company start date value
        this.setCompanyStartDate(parseInt(localStorage.getItem('Empresa')));
        this.empresaTreeViewService.EmpId.subscribe((companyId: number) =>
            this.setCompanyStartDate(companyId)
        );
    }

    getParameterModel(): EstudioParameters {
        if (this.estudioParameterForm.valid) {
            if (this.estudioParameterForm.value.CompanyStartDate !== null) {
                const dateValue = DateHelper.getUTCDateString(
                    this.estudioParameterForm.value.CompanyStartDate._d
                );
                this.estudioParameters.CompanyStartDate = dateValue;
            } else {
                this.estudioParameters.CompanyStartDate = '';
            }

            if (
                this.ReportDateFrom.valid &&
                this.ReportDateFrom.value !== null
            ) {
                const dateValue = DateHelper.getUTCDateString(
                    this.ReportDateFrom.value._d
                );
                this.estudioParameters.ReportStartDate = dateValue;
            } else {
                this.estudioParameters.ReportStartDate = '';
            }

            if (this.ReportDateTo.valid && this.ReportDateTo.value !== null) {
                const dateValue = DateHelper.getUTCDateString(
                    this.ReportDateTo.value._d
                );
                this.estudioParameters.ReportEndDate = dateValue;
            } else {
                this.estudioParameters.ReportEndDate = '';
            }

            this.estudioParameters.CompareWithPreviousYear =
                this.estudioParameterForm.value.CompareWithPrevious;

            if (this.estudioParameterForm.value.CompanyLevel !== null) {
                let levels = '';
                this.estudioParameterForm.value.CompanyLevel.forEach(
                    (level) => {
                        levels += level + ',';
                    }
                );
                this.estudioParameters.CompanyLevels = levels.replace(
                    /,\s*$/,
                    ''
                );
            } else {
                this.estudioParameters.CompanyLevels = '';
            }

            this.estudioParameters.ReportLanguage =
                this.estudioParameterForm.value.ReportLanguage;

            if (this.LeaveType.value !== null) {
                let leaveTypes = '';
                this.LeaveType.value.forEach((leaveType) => {
                    leaveTypes += leaveType + ',';
                });
                this.estudioParameters.LeaveTypes = leaveTypes.replace(
                    /,\s*$/,
                    ''
                );
            } else {
                this.estudioParameters.LeaveTypes = '';
            }

            this.estudioParameters.CompanyId = parseInt(
                localStorage.getItem('Empresa')
            );
            this.estudioParameters.Report5YearAgeGap =
                this.estudioParameterForm.value.Report5YearAgeGap;
            this.estudioParameters.DiasBaja =
                this.estudioParameterForm.value.DiasBaja;  
            return this.estudioParameters;
        } else {
            return null;
        }
    }

    getTemplateModel(): EstudioTemplate {
        this.estudioTemplate.Parameters = this.getParameterModel();
        return this.estudioTemplate;
    }

    getReportList(): Array<EstudioReports> {
        return this.estudioTemplate.ReportList;
    }

    public isFormValid(): boolean {
        this.estudioParameterForm.markAllAsTouched();
        this.ReportDateFrom.markAsTouched();
        this.ReportDateTo.markAsTouched();
        return (
            this.estudioParameterForm.valid &&
            this.ReportDateFrom.valid &&
            this.ReportDateTo.valid
        );
    }

    /**
     * Check if parameters are valid for saving
     */
    public isParametersFormValid(): boolean {
        this.estudioParameterForm.markAllAsTouched();
        return (
            this.estudioParameterForm.valid &&
            this.getParameterModel().CompanyLevels != null &&
            this.getParameterModel().CompanyLevels != '' &&
            this.getParameterModel().LeaveTypes != null &&
            this.getParameterModel().LeaveTypes != ''
        );
    }

    /**
     * Set company start date value
     */
    private setCompanyStartDate(companyId: number) {
        this.getCompanyStartDate(companyId).subscribe(
            (response) => {
                if (response.code === 0) {
                    this.estudioParameterForm.patchValue({
                        CompanyStartDate: response.result,
                    });
                } else {
                    this.sharedService.ShowSnackBar(
                        'Error al obtener la fecha de inicio de la empresa'
                    );
                    console.error(response.message);
                }
            },
            (error) => {
                this.sharedService.ShowSnackBar(
                    'Error al obtener la fecha de inicio de la empresa'
                );
                console.error(error);
            }
        );
    }

    /**
     * Get company start date
     */
    private getCompanyStartDate(
        companyId: number
    ): Observable<RestResponse<Moment>> {
        const url = API.Report.getCompanyStartDate.replace(
            '{companyID}',
            companyId.toString()
        );
        return this.apiService.get(url).pipe(
            map((response: RestResponse<any>) => {
                if (response.result) response.result = moment(response.result);
                return response;
            })
        );
    }

    //#region PREDEFINITIONS

    /**
     * Build predefinition model
     */
    public buildPredefinitionModel(name: string): EstudioEmpresa {
        const empresaId = parseInt(localStorage.getItem('Empresa'));
        const parametros: PredefinitionParameter = {
            FormValues: this.estudioParameterForm.value,
            AttributeList: this.estudioParameters.AttributeList,
            LeaveType: this.LeaveType.value,
        };
        const model = new EstudioEmpresa(
            empresaId,
            name,
            JSON.stringify(parametros)
        );
        return model;
    }

    /**
     * Load predefinition
     */
    public loadPredefinition(predefinition: EstudioEmpresa): void {
        const parameters: PredefinitionParameter = JSON.parse(
            predefinition.parametros
        );
        this.estudioParameterForm.patchValue(parameters.FormValues);
        this.AttributeList = parameters.AttributeList;
        this.LeaveType.setValue(parameters.LeaveType);
    }

    /**
     * Create predefinition. Creating a predefinition with an existing name and a company ID will replace the existing predefinition.
     */
    public createPredefinition(
        predefinition: EstudioEmpresa
    ): Observable<RestResponse<EstudioEmpresa>> {
        const url = API.Report.predefinitionCommonEndpoint;
        return this.apiService.post(url, predefinition);
    }

    /**
     * Get predefinitions of a company
     */
    public getPredefinitions(
        companyId: number
    ): Observable<RestResponse<EstudioEmpresa[]>> {
        const url = API.Report.getPredefinitionsByCompany.replace(
            '{empresaId}',
            companyId.toString()
        );
        return this.apiService.get(url);
    }

    /**
     * Delete predefinition
     */
    public deletePredefinition(id: number): Observable<RestResponse<null>> {
        const url = `${API.Report.predefinitionCommonEndpoint}/${id}`;
        return this.apiService.delete(url);
    }

    //#endregion
}
